import type { DisplayFilterGroup, Session } from '../services/types';
import { AssistantMessage } from './AssistantMessage';
import {
  DisplayMode,
  SystemMessageState,
  useChatContext,
} from '../context/use-chat-context';
import { useEffect, useMemo, useRef, useCallback } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { cn } from '../utils/styles';
import { PERSISTENT_MESSAGE_TIMEOUT } from '../constants/common';
import { useMediaQuery } from '@mantine/hooks';
import { useSuperbarTheme } from '../hooks/useSuperbarTheme';
import { useStreamingText } from './useStreamingText';

interface SystemMessageProps {
  shouldShowAIFilters: boolean;
  session?: Session;
  handleSelectFilter: (updatedFilterGroups: DisplayFilterGroup[]) => void;
  handleRetry?: () => void;
  isCompactView: boolean;
  isTtsMuted?: boolean;
  toggleTtsMute?: () => void;
}

export const SystemMessage = ({
  shouldShowAIFilters,
  session,
  handleSelectFilter,
  handleRetry,
  isCompactView,
  isTtsMuted,
  toggleTtsMute,
}: SystemMessageProps) => {
  const {
    displayMessage,
    systemMessageState,
    superBarViewState,
    setSystemMessageState,
    displayMode,
    showError,
    isLoadingAiFilters,
    setShowError,
    setDisplayMode,
    setDisplayMessage,
    aiFiltersGroupsDisplayed,
    isTextStreamActive,
  } = useChatContext();
  const theme = useSuperbarTheme();

  const isMobile = useMediaQuery('(max-width: 767px)');

  const playBeepPositive = useCallback(() => {
    const audio: HTMLAudioElement | null = document.getElementById(
      'beep-positive'
    ) as HTMLAudioElement;
    audio?.play().catch(() => {});
  }, []);

  const previousSystemMessageStateRef =
    useRef<SystemMessageState>(systemMessageState);
  const previousIsLoadingAiFiltersRef = useRef<boolean>(isLoadingAiFilters);
  const hasShownAiFiltersRef = useRef<boolean>(false);

  // Check if we have displayable text when streaming
  const { hasDisplayableText } = useStreamingText({
    accumulatedText: displayMessage?.systemAnswer || '',
    isStreamActive: isTextStreamActive,
  });

  const shouldHideSystemMessage = useMemo(() => {
    // Hide if state is HIDDEN
    if (systemMessageState === SystemMessageState.HIDDEN) return true;

    // Hide if loading AI filters (except for exit intent)
    if (
      isLoadingAiFilters &&
      systemMessageState !== SystemMessageState.EXIT_INTENT
    )
      return true;

    // Hide if compact view (except for exit intent)
    if (isCompactView && systemMessageState !== SystemMessageState.EXIT_INTENT)
      return true;

    // Hide ASSISTANT messages when streaming but no displayable text yet
    return (
      systemMessageState === SystemMessageState.ASSISTANT &&
      isTextStreamActive &&
      !hasDisplayableText &&
      !displayMessage?.systemAnswer?.length
    );
  }, [
    systemMessageState,
    isLoadingAiFilters,
    isCompactView,
    isTextStreamActive,
    hasDisplayableText,
    displayMessage?.systemAnswer?.length,
  ]);

  const handleClose = () => {
    if (aiFiltersGroupsDisplayed && aiFiltersGroupsDisplayed.length > 0) {
      setSystemMessageState(SystemMessageState.AI_FILTERS);
      setShowError(false);
      return;
    }

    setShowError(false);
    setSystemMessageState(SystemMessageState.HIDDEN);
    setDisplayMode(DisplayMode.ASSISTANT);
    setDisplayMessage(undefined);
  };

  useEffect(() => {
    if (systemMessageState === SystemMessageState.EXIT_INTENT) {
      return;
    }

    if (showError) {
      setSystemMessageState(SystemMessageState.ERROR);
      return;
    }

    if (displayMode === DisplayMode.NO_RESULTS) {
      setSystemMessageState(SystemMessageState.NO_RESULTS);
      return;
    }

    if (shouldShowAIFilters && aiFiltersGroupsDisplayed.length > 0) {
      setSystemMessageState(SystemMessageState.AI_FILTERS);

      return;
    }

    if (displayMessage?.systemAnswer?.length) {
      // Only show ASSISTANT state if we have displayable text when streaming
      if (isTextStreamActive && !hasDisplayableText) {
        setSystemMessageState(SystemMessageState.HIDDEN);
      } else {
        setSystemMessageState(SystemMessageState.ASSISTANT);
      }
      return;
    }

    // Don't hide persistent messages (autoHideTimeout === -1)
    if (displayMessage?.autoHideTimeout === PERSISTENT_MESSAGE_TIMEOUT) {
      return;
    }

    setSystemMessageState(SystemMessageState.HIDDEN);
  }, [
    systemMessageState,
    shouldShowAIFilters,
    displayMessage,
    displayMode,
    showError,
    setSystemMessageState,
    aiFiltersGroupsDisplayed.length,
    hasDisplayableText,
    isTextStreamActive,
  ]);

  useEffect(() => {
    const previousState = previousSystemMessageStateRef.current;
    const currentState = systemMessageState;
    const wasLoadingAiFilters = previousIsLoadingAiFiltersRef.current;
    const isCurrentlyLoadingAiFilters = isLoadingAiFilters;

    // Play beep if:
    // 1. State changed to a visible state (not HIDDEN, ERROR, or NO_RESULTS) and not currently loading
    // 2. AI filters finished loading (was loading, now not loading) and current state is visible and not error/no results
    // For AI_FILTERS: only play on first time or when returning from a different state
    const shouldPlayBeep =
      (previousState !== currentState &&
        currentState !== SystemMessageState.HIDDEN &&
        currentState !== SystemMessageState.ERROR &&
        currentState !== SystemMessageState.NO_RESULTS &&
        !isCurrentlyLoadingAiFilters &&
        (currentState !== SystemMessageState.AI_FILTERS ||
          !hasShownAiFiltersRef.current ||
          previousState !== SystemMessageState.AI_FILTERS)) ||
      (wasLoadingAiFilters &&
        !isCurrentlyLoadingAiFilters &&
        currentState !== SystemMessageState.HIDDEN &&
        currentState !== SystemMessageState.ERROR &&
        currentState !== SystemMessageState.NO_RESULTS &&
        (currentState !== SystemMessageState.AI_FILTERS ||
          !hasShownAiFiltersRef.current ||
          previousState !== SystemMessageState.AI_FILTERS));

    if (shouldPlayBeep && !isCompactView) {
      playBeepPositive();
    }

    if (
      currentState === SystemMessageState.AI_FILTERS &&
      !isCurrentlyLoadingAiFilters
    ) {
      hasShownAiFiltersRef.current = true;
    }

    // Update the refs with current states
    previousSystemMessageStateRef.current = currentState;
    previousIsLoadingAiFiltersRef.current = isCurrentlyLoadingAiFilters;
  }, [systemMessageState, playBeepPositive, isLoadingAiFilters, isCompactView]);

  // Create a unique key for the animation that includes the message ID
  // This ensures the animation triggers when a new message arrives, even if the state is the same
  const messageKey = useMemo(
    () =>
      displayMessage?.id
        ? `system-message-${systemMessageState}-${displayMessage.id}`
        : `system-message-${systemMessageState}-${Date.now()}`,
    [displayMessage?.id, systemMessageState]
  );

  return (
    <AnimatePresence mode="wait">
      <audio id="beep-positive" src="/sounds/beep-positive.wav" />

      {!shouldHideSystemMessage && (
        <m.div
          className="caddie-system-message-container"
          initial={{ height: 0, opacity: 0, fontSize: 0 }}
          animate={{
            height: 'auto',
            opacity: 1,
            fontSize: '16px',
            transition: {
              duration: 0.4,
              ease: 'easeInOut',
              type: 'spring',
              delay: 0.3,
            },
          }}
          exit={{
            height: 0,
            opacity: 0,
            transition: {
              duration: 0.5,
              ease: 'easeInOut',
              type: 'spring',
            },
          }}
          id={messageKey}
          key={messageKey}
        >
          <div
            className={cn(
              'caddie-system-message-parent-container caddietw-relative caddietw-border-b-[1px]',
              theme.components.systemMessage.className
            )}
            style={{
              borderBottomColor:
                theme.components.systemMessage.borderBottomColor,
              background: theme.components.systemMessage.background,
              borderTopLeftRadius: theme.components.systemMessage.radiusTop,
              borderTopRightRadius: theme.components.systemMessage.radiusTop,
            }}
          >
            <div
              className={cn(
                'caddietw-px-5 caddietw-pt-[14px] caddietw-pb-[14px]',
                isMobile && 'caddietw-px-6 caddietw-py-6',
                systemMessageState === SystemMessageState.AI_FILTERS &&
                  'caddietw-pb-0 md:caddietw-pb-0'
              )}
              style={{ transform: 'translateZ(0)' }}
            >
              {systemMessageState === SystemMessageState.EXIT_INTENT &&
                displayMessage && (
                  <AssistantMessage
                    message={displayMessage.systemAnswer || ''}
                    storeId={session?.store?.id}
                    onClose={handleClose}
                    isTtsMuted={isTtsMuted}
                    toggleTtsMute={toggleTtsMute}
                  />
                )}
              {systemMessageState === SystemMessageState.ASSISTANT &&
                displayMessage && (
                  <AssistantMessage
                    message={displayMessage.systemAnswer || ''}
                    isStreamActive={isTextStreamActive}
                    autoHideTimeout={displayMessage.autoHideTimeout}
                    citations={displayMessage.citations}
                    cta={displayMessage.suggestedCTA}
                    nonDefinedCTA={displayMessage.nonDefinedCTA}
                    storeId={session?.store?.id}
                    hasCode={displayMessage.hasCode}
                    onClose={handleClose}
                    isTtsMuted={isTtsMuted}
                    toggleTtsMute={toggleTtsMute}
                  />
                )}
              {systemMessageState === SystemMessageState.NO_RESULTS && (
                <AssistantMessage
                  message="I can't seem to find that. try asking in a different way"
                  storeId={session?.store?.id}
                  onClose={handleClose}
                  isTtsMuted={isTtsMuted}
                  toggleTtsMute={toggleTtsMute}
                />
              )}
              {systemMessageState === SystemMessageState.ERROR && (
                <AssistantMessage
                  message="Sorry, I am having trouble processing your request. Please try again."
                  storeId={session?.store?.id}
                  onRetry={handleRetry}
                  onClose={handleClose}
                  isTtsMuted={isTtsMuted}
                  toggleTtsMute={toggleTtsMute}
                />
              )}
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default SystemMessage;
